import { useState, useCallback } from 'react';

/**
 * Custom hook for list API fetching that follows the exact pattern from OrdersReport
 *
 * Pattern:
 * - isLoading: boolean state for loading
 * - isError: boolean state for errors
 * - data: the fetched data
 * - fetchData: async function that handles the fetch with proper error handling
 */
export function useListFetch<T = any>() {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchData = useCallback(async (fetchFn: () => Promise<T>) => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await fetchFn();
      setData(response);
      return response;
    } catch (error) {
      setIsError(true);
      console.error('Error fetching data:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setIsLoading(false);
    setIsError(false);
  }, []);

  return {
    data,
    isLoading,
    isError,
    fetchData,
    reset,
  };
}
